import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Holding, CoinListItem } from '../../types';
import { CardHeader } from '../ui/Card';
import { cgGet } from '../../utils/coingecko';
import { isMetal, METAL_DISPLAY } from '../../utils/metals';
import { isStockOrETF, yahooTicker, ASSET_BADGE, SUPPORTED_STOCK_ITEMS, getAssetType } from '../../utils/stocks';
import { convertToUsd } from '../../hooks/useExchangeRates';
import { loadCoinImages } from '../../utils/coinImageCache';
import { isNarrowViewport } from '../../utils/viewport';
import { formatPercent, formatDateShort } from '../../utils/formatters';
import { useCoinList } from '../../hooks/useCoinList';
import { useCurrency } from '../../context/CurrencyContext';
import {
  Search, ChevronDown, Loader2, AlertCircle, Trophy,
  TrendingUp, TrendingDown, ArrowLeftRight, RefreshCw, Plus, X,
} from 'lucide-react';

// ── Types & constants ─────────────────────────────────────────────────────────

export type CompareRange = '7D' | '1M' | '3M' | '1Y';

export interface AssetOption {
  id: string;
  name: string;
  symbol: string;
  assetType: 'crypto' | 'stock' | 'etf' | 'metal';
  image: string;
}

export interface PricePt { ts: number; price: number; }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComparePoint = Record<string, any>;
interface AssetStats { startPrice: number; endPrice: number; absChange: number; pctChange: number; }
interface CompareResult {
  loading: boolean; error: string | null;
  points: ComparePoint[]; stats: (AssetStats | null)[];
}

export const RANGES: CompareRange[] = ['7D', '1M', '3M', '1Y'];
const CG_DAYS: Record<CompareRange, number> = { '7D': 7, '1M': 30, '3M': 90, '1Y': 365 };

// teal, purple, amber, coral, blue
const ASSET_COLORS = ['#10b981', '#38bdf8', '#f59e0b', '#f87171', '#3b82f6'];
const ASSET_LABELS = ['A', 'B', 'C', 'D', 'E'];
const MAX_ASSETS = 5;
const MIN_ASSETS = 2;

const EMPTY: CompareResult = { loading: false, error: null, points: [], stats: [] };

// ── Local price-history cache (localStorage) ───────────────────────────────────

function cacheKey(coinId: string, range: CompareRange): string {
  return `cg_compare_${coinId}_${range}`;
}

function loadCache(coinId: string, range: CompareRange): PricePt[] | null {
  try {
    const raw = localStorage.getItem(cacheKey(coinId, range));
    if (!raw) return null;
    const { pts, savedAt } = JSON.parse(raw) as { pts: PricePt[]; savedAt: number };
    if (Date.now() - savedAt > 3_600_000) return null; // expire after 1 hour
    return pts;
  } catch {
    return null;
  }
}

function saveCache(coinId: string, range: CompareRange, pts: PricePt[]): void {
  try {
    localStorage.setItem(cacheKey(coinId, range), JSON.stringify({ pts, savedAt: Date.now() }));
  } catch { /* ignore quota errors */ }
}

// ── Data fetching ─────────────────────────────────────────────────────────────

export async function fetchCgHistory(coinId: string, range: CompareRange): Promise<PricePt[]> {
  try {
    const { data } = await cgGet(`/coins/${coinId}/market_chart`, {
      // interval omitted so the Pro API auto-selects granularity.
      params: { vs_currency: 'usd', days: CG_DAYS[range] },
      timeout: 15_000,
    });
    const pts = ((data as any).prices as [number, number][]).map(([ts, price]) => ({ ts, price }));
    console.log(`[AssetComparison] ${coinId} ${range}: fetched ${pts.length} real price points`);
    saveCache(coinId, range, pts);
    return pts;
  } catch (err) {
    console.warn(`[AssetComparison] ${coinId} ${range}: API fetch failed, trying localStorage cache`, err);
    const cached = loadCache(coinId, range);
    if (cached && cached.length > 0) {
      console.log(`[AssetComparison] ${coinId} ${range}: using ${cached.length} cached points from localStorage`);
      return cached;
    }
    throw err;
  }
}

// Compare's ranges → the Yahoo `range` param. 7D and 1M both use the smallest
// Yahoo window (1mo); mergeHistoryMulti trims to the overlapping days anyway.
const YAHOO_RANGE: Record<CompareRange, string> = {
  '7D': '1mo', '1M': '1mo', '3M': '3mo', '1Y': '1y',
};

export async function fetchStockHistory(assetId: string, range: CompareRange): Promise<PricePt[]> {
  const ticker = yahooTicker(assetId); // etf:VAS → VAS.AX
  try {
    const { data } = await axios.get('/api/stock-history', {
      params: { ticker, range: YAHOO_RANGE[range], interval: '1d' },
      timeout: 15_000,
      headers: { Accept: 'application/json' },
    });
    const timestamp: number[]           = Array.isArray(data?.timestamp) ? data.timestamp : [];
    const close:     (number | null)[]  = Array.isArray(data?.close)     ? data.close     : [];
    const currency:  string             = typeof data?.currency === 'string' ? data.currency : '';

    if (!currency) throw new Error('Stock history response is missing a currency.');

    // Yahoo reports prices in the ticker's native currency; store everything in USD
    // (like crypto) so the series line up; the performance cards convert to the
    // display currency with fmt().
    // Compute the FX factor once (USD per 1 unit of native currency).
    const usdPerUnit = convertToUsd(1, currency);
    if (usdPerUnit === null) {
      throw new Error(`No FX rate to convert ${currency} to USD.`);
    }

    const pts: PricePt[] = [];
    for (let i = 0; i < timestamp.length; i++) {
      const c = close[i];
      // Yahoo pads holidays/halts with null — skip them (seconds → ms for ts).
      if (c === null || c === undefined || !isFinite(c)) continue;
      pts.push({ ts: timestamp[i] * 1000, price: c * usdPerUnit });
    }

    if (!pts.length) throw new Error('No price history available.');
    console.log(`[AssetComparison] ${assetId} ${range}: fetched ${pts.length} stock price points (${currency})`);
    saveCache(assetId, range, pts);
    return pts;
  } catch (err) {
    console.warn(`[AssetComparison] ${assetId} ${range}: stock history fetch failed, trying localStorage cache`, err);
    const cached = loadCache(assetId, range);
    if (cached && cached.length > 0) return cached;
    throw err;
  }
}

export async function fetchHistory(asset: AssetOption, range: CompareRange): Promise<PricePt[]> {
  if (isMetal(asset.id)) {
    throw new Error(`Historical price data is not available for ${asset.name}.`);
  }
  if (isStockOrETF(asset.id)) {
    return fetchStockHistory(asset.id, range);
  }
  return fetchCgHistory(asset.id, range);
}

// ── Merge & normalise (N assets) ──────────────────────────────────────────────

function toDay(tsMs: number) { return new Date(tsMs).toISOString().split('T')[0]; }

function mergeHistoryMulti(allPts: PricePt[][]): { points: ComparePoint[]; stats: AssetStats[] } | null {
  if (allPts.some((pts) => !pts.length)) return null;

  const maps = allPts.map((pts) => {
    const map = new Map<string, number>();
    pts.forEach((p) => map.set(toDay(p.ts), p.price));
    return map;
  });

  const allDaysSet = new Set<string>();
  maps.forEach((m) => Array.from(m.keys()).forEach((k) => allDaysSet.add(k)));
  const allDays = Array.from(allDaysSet).sort();

  const bases = allPts.map((pts) => pts[0].price);
  const ends  = allPts.map((pts) => pts[pts.length - 1].price);
  const lasts: (number | null)[] = allPts.map(() => null);

  const points: ComparePoint[] = allDays.map((day) => {
    const point: ComparePoint = { label: formatDateShort(day) };
    maps.forEach((map, i) => {
      const v = map.get(day);
      if (v !== undefined) lasts[i] = v;
      const base = bases[i];
      point[`asset_${i}`] = lasts[i] !== null && base > 0
        ? +((lasts[i]! - base) / base * 100).toFixed(3)
        : null;
    });
    return point;
  });

  const mkStats = (base: number, end: number): AssetStats => ({
    startPrice: base, endPrice: end,
    absChange: end - base,
    pctChange: base > 0 ? (end - base) / base * 100 : 0,
  });

  return { points, stats: allPts.map((_, i) => mkStats(bases[i], ends[i])) };
}

// ── Shared icon ───────────────────────────────────────────────────────────────

export function AssetIcon({ assetType, coinId, image, size = 'w-7 h-7' }: {
  assetType: string; coinId: string; image: string; size?: string;
}) {
  if (assetType === 'metal') {
    const d = METAL_DISPLAY[coinId];
    if (!d) return <div className={`${size} rounded-full bg-slate-700 flex-shrink-0`} />;
    return (
      <div className={`${size} rounded-full flex items-center justify-center font-bold flex-shrink-0`}
        style={{ backgroundColor: d.bg, border: `1.5px solid ${d.border}` }}>
        <span style={{ color: d.color, fontSize: '10px' }}>{d.label}</span>
      </div>
    );
  }
  if (assetType === 'stock' || assetType === 'etf') {
    const etf = assetType === 'etf';
    return (
      <div className={`${size} rounded-full flex items-center justify-center flex-shrink-0`}
        style={{ backgroundColor: etf ? '#8B5CF615' : '#3B82F615', border: `1.5px solid ${etf ? '#8B5CF640' : '#3B82F640'}` }}>
        <TrendingUp style={{ color: etf ? '#8B5CF6' : '#3B82F6', width: '55%', height: '55%' }} />
      </div>
    );
  }
  return image ? (
    <img src={image} alt="" className={`${size} rounded-full flex-shrink-0 bg-slate-700`}
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
  ) : (
    <div className={`${size} rounded-full bg-slate-700 flex-shrink-0`} />
  );
}

// ── Asset picker ──────────────────────────────────────────────────────────────

export function AssetPicker({ accentColor, selected, onSelect, holdings, coins, disabledIds = [] }: {
  accentColor: string; selected: AssetOption | null;
  onSelect: (a: AssetOption) => void; holdings: Holding[];
  coins: CoinListItem[]; disabledIds?: string[];
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const ref      = useRef<HTMLDivElement>(null);
  const menuRef  = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const imageCache = useMemo(() => loadCoinImages(), []);
  const { fmt } = useCurrency();

  // Viewport-relative position of the dropdown menu. The menu is rendered into a
  // portal on `document.body` so that overflow-clipping parents (e.g. the
  // PlanAccordion's `overflow-hidden`, the sticky page chrome, etc.) cannot trap
  // or hide it. We use `position: fixed` and recompute on scroll/resize so the
  // menu stays glued to the trigger as the user interacts with the page.
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  useEffect(() => {
    if (!open) { setMenuRect(null); return; }
    const update = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      setMenuRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
    };
    update();
    // capture-phase scroll so nested scroll containers also trigger reposition
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // Outside-click closes the menu. The portaled menu lives outside `ref`'s
  // subtree, so we also have to allow clicks on `menuRef` and its descendants.
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false); setQuery('');
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Desktop only. On a phone the keyboard would cover the list the user just
  // asked to see; tapping the search box still focuses it normally. Evaluated
  // at open time so a rotation cannot leave a stale answer behind. Mirrors the
  // same decision in CoinSelector.
  useEffect(() => {
    if (!open || isNarrowViewport()) return;
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  const q = query.toLowerCase();

  const filteredHoldings = holdings.filter((h) =>
    !disabledIds.includes(h.coinId) && (!q || h.name.toLowerCase().includes(q) || h.symbol.toLowerCase().includes(q))
  );

  const filteredCoins = useMemo(() => {
    const heldIds = new Set(holdings.map((h) => h.coinId));
    const base = coins.filter((c) => !disabledIds.includes(c.id) && !heldIds.has(c.id));
    if (!q) return base.slice(0, 30);
    return base.filter((c) => c.name.toLowerCase().includes(q) || c.symbol.toLowerCase().includes(q)).slice(0, 30);
  }, [coins, disabledIds, q, holdings]);

  // Curated stock/ETF universe (230). Unsearched, cap to a short preview so the menu
  // stays small; a query reaches the whole list (matched on name OR ticker), so
  // "nasdaq" finds QQQ and "cba" finds CBA.AX.
  const filteredStocks = useMemo(() => {
    const heldIds = new Set(holdings.map((h) => h.coinId));
    const base = SUPPORTED_STOCK_ITEMS.filter((s) => !disabledIds.includes(s.id) && !heldIds.has(s.id));
    if (!q) return base.slice(0, 20);
    return base.filter((s) => s.name.toLowerCase().includes(q) || s.symbol.toLowerCase().includes(q)).slice(0, 30);
  }, [disabledIds, q, holdings]);

  const pick = (opt: AssetOption) => { onSelect(opt); setOpen(false); setQuery(''); };

  const rowClass = (id: string) =>
    `w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[rgb(var(--overlay)/0.05)] transition-colors ${selected?.id === id ? 'bg-[rgb(var(--overlay)/0.09)]' : ''}`;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-left hover:border-slate-600 focus:outline-none transition-colors">
        {selected ? (
          <>
            <AssetIcon assetType={selected.assetType} coinId={selected.id} image={selected.image} size="w-6 h-6" />
            <span className="font-semibold text-slate-100 truncate flex-1">{selected.name}</span>
            <span className="text-xs text-slate-500 flex-shrink-0">{selected.symbol}</span>
          </>
        ) : (
          <span className="text-slate-500 flex-1">Select an asset…</span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && menuRect && createPortal(
        <div
          ref={menuRef}
          className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
          style={{ position: 'fixed', top: menuRect.top, left: menuRect.left, width: menuRect.width, zIndex: 1000 }}
        >
          <div className="p-2 border-b border-slate-700">
            <div className="flex items-center gap-2 bg-slate-900 rounded-lg px-3 py-2">
              <Search className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              {/* 16px minimum below md, or the browser zooms the page on focus. */}
              <input ref={inputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="flex-1 bg-transparent text-sm max-md:text-[16px] text-slate-100 placeholder-slate-500 focus:outline-none" />
            </div>
          </div>

          {/* dvh below md so the list is measured against what is actually
              visible once the keyboard and browser chrome take their share. */}
          <div className="overflow-y-auto max-h-64 max-md:max-h-[45dvh]">
            {filteredHoldings.length > 0 && (
              <>
                <div className="px-3 py-1.5 bg-slate-900/60 border-b border-slate-700/50">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Your Portfolio</span>
                </div>
                {filteredHoldings.map((h) => (
                  <button key={h.coinId} type="button" onClick={() => pick({
                    id: h.coinId, name: h.name, symbol: h.symbol,
                    assetType: h.assetType, image: imageCache[h.coinId] ?? '',
                  })} className={rowClass(h.coinId)}>
                    <AssetIcon assetType={h.assetType} coinId={h.coinId} image={imageCache[h.coinId] ?? ''} size="w-6 h-6" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-slate-100 truncate block">{h.name}</span>
                      <span className="text-xs text-slate-500">{h.symbol}</span>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${ASSET_BADGE[h.assetType].className}`}>
                      {ASSET_BADGE[h.assetType].label}
                    </span>
                  </button>
                ))}
              </>
            )}

            {filteredCoins.length > 0 && (
              <>
                <div className="px-3 py-1.5 bg-slate-900/60 border-b border-slate-700/50">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Top Crypto</span>
                </div>
                {filteredCoins.map((c) => (
                  <button key={c.id} type="button"
                    onClick={() => pick({ id: c.id, name: c.name, symbol: c.symbol, assetType: 'crypto', image: c.image })}
                    className={rowClass(c.id)}>
                    <AssetIcon assetType="crypto" coinId={c.id} image={c.image} size="w-6 h-6" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-slate-100 truncate block">{c.name}</span>
                      <span className="text-xs text-slate-500">{c.symbol}</span>
                    </div>
                    {/* USD from useCoinList; fmt() converts and labels it. */}
                    {c.currentPrice > 0 && (
                      <span className="text-xs text-slate-500 font-mono flex-shrink-0">
                        {fmt(c.currentPrice)}
                      </span>
                    )}
                  </button>
                ))}
              </>
            )}

            {filteredStocks.length > 0 && (
              <>
                <div className="px-3 py-1.5 bg-slate-900/60 border-b border-slate-700/50">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Stocks &amp; ETFs</span>
                </div>
                {filteredStocks.map((s) => {
                  const at = getAssetType(s.id); // 'stock' | 'etf'
                  return (
                    <button key={s.id} type="button"
                      onClick={() => pick({ id: s.id, name: s.name, symbol: s.symbol, assetType: at, image: '' })}
                      className={rowClass(s.id)}>
                      <AssetIcon assetType={at} coinId={s.id} image="" size="w-6 h-6" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-100 truncate block">{s.name}</span>
                        <span className="text-xs text-slate-500">{s.symbol}</span>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${ASSET_BADGE[at].className}`}>
                        {ASSET_BADGE[at].label}
                      </span>
                    </button>
                  );
                })}
              </>
            )}

            {filteredHoldings.length === 0 && filteredCoins.length === 0 && filteredStocks.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-500">No assets found</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Performance card ──────────────────────────────────────────────────────────

function PerformanceCard({ accentColor, asset, stats, isWinner, holding, fmt, fmtDisplay }: {
  accentColor: string; asset: AssetOption; stats: AssetStats;
  isWinner: boolean; holding?: Holding;
  /** Converts a USD value at today's rate — for class B figures. */
  fmt: (n: number) => string;
  /** Formats a value already in the display currency — for class A and C figures. */
  fmtDisplay: (n: number) => string;
}) {
  const up = stats.pctChange >= 0;
  // Start and End are USD prices, converted at TODAY's rate (both of them). The
  // Return beneath them is a USD-based percentage, so converting both ends at the
  // same rate is what keeps Start, End and Return consistent with each other.
  const fmtPrice = (p: number) => fmt(p);

  return (
    <div className="flex-1 min-w-[180px] bg-slate-800/50 rounded-xl border border-slate-700/60 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: accentColor }} />
          <AssetIcon assetType={asset.assetType} coinId={asset.id} image={asset.image} size="w-7 h-7" />
          <div className="min-w-0">
            <p className="font-semibold text-slate-100 text-sm truncate">{asset.name}</p>
            <p className="text-xs text-slate-500">{asset.symbol}</p>
          </div>
        </div>
        {isWinner && (
          <div className="flex items-center gap-1 text-xs font-semibold text-yellow-500 bg-yellow-500/10 px-2 py-1 rounded-full border border-yellow-500/20 flex-shrink-0">
            <Trophy className="w-3 h-3" />
            Winner
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex justify-between items-center text-xs">
          <span className="text-slate-500">Start</span>
          <span className="text-slate-400 font-mono">{fmtPrice(stats.startPrice)}</span>
        </div>
        <div className="flex justify-between items-center text-xs">
          <span className="text-slate-500">End</span>
          <span className="text-slate-300 font-mono font-medium">{fmtPrice(stats.endPrice)}</span>
        </div>
        <div className="flex justify-between items-center pt-1 border-t border-slate-700/40">
          <span className="text-xs text-slate-500">Return</span>
          <div className="flex items-center gap-1.5">
            {up
              ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
              : <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
            <span className={`text-sm font-bold ${up ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatPercent(stats.pctChange)}
            </span>
          </div>
        </div>
      </div>

      {holding && (
        <div className="pt-3 border-t border-slate-700/50 space-y-1.5">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Your Position</p>
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500">Held</span>
            <span className="text-slate-300 font-mono">{holding.amount.toFixed(4)} {holding.symbol}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-500">P&amp;L</span>
            <span className={`text-sm font-semibold ${holding.display.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {holding.display.pnl >= 0 ? '+' : ''}{fmtDisplay(holding.display.pnl)}
              <span className="text-xs font-normal ml-1 opacity-70">({formatPercent(holding.display.pnlPercent)})</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Custom tooltip ────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CompareTooltip({ active, payload, label, assets }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 shadow-xl text-xs space-y-1.5 min-w-[170px]">
      <p className="text-slate-400 font-medium pb-1 border-b border-slate-700/60">{label}</p>
      {(assets as AssetOption[]).map((asset, i) => {
        const entry = payload.find((p: { dataKey: string }) => p.dataKey === `asset_${i}`);
        const val = entry?.value as number | null | undefined;
        if (val === null || val === undefined) return null;
        return (
          <div key={asset.id} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ASSET_COLORS[i] }} />
              <span className="text-slate-300 truncate max-w-[90px]">{asset.name}</span>
            </div>
            <span className={`font-semibold ${val >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {val >= 0 ? '+' : ''}{val.toFixed(2)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function AssetComparison({
  holdings,
  defaultAssetA,
  compact = false,
}: {
  holdings: Holding[];
  /** Pre-fill the first picker (e.g. the coin currently being viewed on its detail page). */
  defaultAssetA?: AssetOption;
  /** Slim embedded variant: hides the card header, add/remove buttons, legend and
   *  performance cards, and uses a smaller chart. The two pickers, the range
   *  selector and the chart remain — the only essentials called out in the spec. */
  compact?: boolean;
}) {
  const { fmt, fmtDisplay } = useCurrency();
  const { coins } = useCoinList();

  // Selector state: array of chosen assets (null = slot not yet filled).
  // When `defaultAssetA` is provided (compact mode on a coin page), slot 0 starts
  // pre-filled with that coin. The user can still change it via the picker.
  const [assets,          setAssets]          = useState<(AssetOption | null)[]>(() => [defaultAssetA ?? null, null]);
  // Snapshot of assets at the time Compare was last clicked — drives chart + cards
  const [comparedAssets,  setComparedAssets]  = useState<AssetOption[]>([]);
  const [range,           setRange]           = useState<CompareRange>('1M');
  const [result,          setResult]          = useState<CompareResult>(EMPTY);

  const addAsset    = () => setAssets((prev) => [...prev, null]);
  const removeAsset = (idx: number) => setAssets((prev) => prev.filter((_, i) => i !== idx));
  const updateAsset = (idx: number, asset: AssetOption) =>
    setAssets((prev) => prev.map((a, i) => (i === idx ? asset : a)));

  const selectedAssets = assets.filter(Boolean) as AssetOption[];
  const canCompare     = selectedAssets.length >= MIN_ASSETS && new Set(selectedAssets.map((a) => a.id)).size === selectedAssets.length;

  const findHolding = (id: string) => holdings.find((h) => h.coinId === id);

  const handleCompare = useCallback(async () => {
    if (!canCompare) return;
    setResult({ ...EMPTY, loading: true });
    try {
      const settled = await Promise.allSettled(
        selectedAssets.map((a) => fetchHistory(a, range))
      );

      const errors: string[] = [];
      const allPts = settled.map((r, i) => {
        if (r.status === 'fulfilled') return r.value;
        const msg = r.reason instanceof Error ? r.reason.message : 'Failed to load data';
        errors.push(`${selectedAssets[i].name}: ${msg}`);
        return [] as PricePt[];
      });

      if (allPts.some((pts) => pts.length === 0)) {
        setResult({ ...EMPTY, error: errors.join(' · ') || 'Failed to load price history.' });
        return;
      }

      const merged = mergeHistoryMulti(allPts);
      if (!merged) { setResult({ ...EMPTY, error: 'Not enough data to compare.' }); return; }
      setComparedAssets(selectedAssets);
      setResult({ loading: false, error: null, points: merged.points, stats: merged.stats });
    } catch (err) {
      setResult({ ...EMPTY, error: err instanceof Error ? err.message : 'Failed to load comparison data.' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, range, canCompare]);

  // Sample chart data to avoid rendering too many SVG path points
  const chartData = useMemo(() => {
    const pts = result.points;
    if (pts.length <= 90) return pts;
    const step = Math.ceil(pts.length / 90);
    return pts.filter((_, i) => i % step === 0 || i === pts.length - 1);
  }, [result.points]);

  const bestPct = result.stats.length
    ? Math.max(...result.stats.filter(Boolean).map((s) => s!.pctChange))
    : -Infinity;

  // IDs already selected (for disabling in other pickers)
  const selectedIds = assets.map((a) => a?.id).filter(Boolean) as string[];

  const hasResults = result.points.length > 0 && !result.error;

  return (
    <div>
      {!compact && (
        <CardHeader
          title="Compare Assets"
          subtitle="Normalised % return from period start - compare up to 5 assets"
        />
      )}

      {/* Asset selectors */}
      <div className={`${compact ? 'mb-3' : 'mb-4'} space-y-3`}>
        <div className="flex flex-wrap gap-3">
          {assets.map((asset, i) => (
            <div key={i} className="flex-1 min-w-[180px] space-y-1.5">
              {/* Label row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ASSET_COLORS[i] }} />
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Asset {ASSET_LABELS[i]}
                  </span>
                </div>
                {!compact && assets.length > MIN_ASSETS && (
                  <button
                    type="button"
                    onClick={() => removeAsset(i)}
                    className="p-0.5 rounded text-slate-600 hover:text-red-400 transition-colors"
                    title="Remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <AssetPicker
                accentColor={ASSET_COLORS[i]}
                selected={asset}
                onSelect={(a) => updateAsset(i, a)}
                holdings={holdings}
                coins={coins}
                disabledIds={selectedIds.filter((id) => id !== asset?.id)}
              />
            </div>
          ))}
        </div>

        {/* Add asset button */}
        {!compact && assets.length < MAX_ASSETS && (
          <button
            type="button"
            onClick={addAsset}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 border border-dashed border-slate-700 hover:border-slate-500 px-3 py-2 rounded-xl transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Asset
          </button>
        )}
      </div>

      {/* Range + Compare */}
      <div className={`flex flex-wrap items-center gap-3 ${compact ? 'mb-4' : 'mb-6'}`}>
        <div className="flex gap-1 bg-slate-800 rounded-xl p-1">
          {RANGES.map((r) => (
            <button key={r} type="button" onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                range === r ? 'bg-slate-700 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'
              }`}>
              {r}
            </button>
          ))}
        </div>
        <button type="button" onClick={handleCompare}
          disabled={!canCompare || result.loading}
          className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-[#e8e8e8] disabled:opacity-40 disabled:cursor-not-allowed text-[#0f0f0f] text-sm font-semibold rounded-xl transition-colors">
          {result.loading
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</>
            : <>{hasResults && <RefreshCw className="w-3.5 h-3.5" />} Compare</>
          }
        </button>
      </div>

      {/* Error */}
      {result.error && (
        <div className="flex items-center gap-2.5 p-4 bg-red-500/5 border border-red-500/15 rounded-xl text-sm text-red-400 mb-6">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {result.error}
        </div>
      )}

      {/* Empty prompt */}
      {!result.loading && !result.error && result.points.length === 0 && (
        compact ? (
          <p className="text-xs text-slate-500 italic">
            Pick an asset for slot B and click Compare to see relative performance.
          </p>
        ) : (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="p-4 bg-slate-800 rounded-2xl mb-4">
              <ArrowLeftRight className="w-7 h-7 text-slate-600" />
            </div>
            <p className="text-slate-400 font-medium text-sm">Select assets and click Compare</p>
            <p className="text-slate-600 text-xs mt-1">All lines start at 0% so you can compare relative performance</p>
          </div>
        )
      )}

      {/* Chart + summary */}
      {hasResults && (
        <>
          {/* Legend (full mode only — compact relies on the picker color dots) */}
          {!compact && (
            <div className="flex items-center gap-6 mb-4 flex-wrap">
              {comparedAssets.map((asset, i) => (
                <div key={asset.id} className="flex items-center gap-2">
                  <div className="w-6 h-0.5 rounded-full" style={{ backgroundColor: ASSET_COLORS[i] }} />
                  <span className="text-sm text-slate-300 font-medium">{asset.name}</span>
                  <span className="text-xs text-slate-500">({asset.symbol})</span>
                </div>
              ))}
            </div>
          )}

          <ResponsiveContainer width="100%" height={compact ? 200 : 260}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false}
                tickLine={false} interval="preserveStartEnd" minTickGap={50} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`} width={58} />
              <ReferenceLine y={0} stroke="#334155" strokeDasharray="4 4" />
              <Tooltip content={(props) => (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                <CompareTooltip {...(props as any)} assets={comparedAssets} />
              )} />
              {comparedAssets.map((asset, i) => (
                <Line
                  key={asset.id}
                  type="monotone"
                  dataKey={`asset_${i}`}
                  stroke={ASSET_COLORS[i]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ fill: ASSET_COLORS[i], strokeWidth: 0, r: 4 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>

          {/* Currency caveat: each line is normalised in its own native currency. */}
          <p className="text-xs text-slate-500 mt-2">
            Returns are shown in each asset's native currency and exclude currency movement.
          </p>

          {/* Performance summary cards (full mode only) */}
          {!compact && result.stats.length > 0 && (
            <>
              <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-slate-800">
                {comparedAssets.map((asset, i) => {
                  const stats = result.stats[i];
                  if (!stats) return null;
                  return (
                    <PerformanceCard
                      key={asset.id}
                      accentColor={ASSET_COLORS[i]}
                      asset={asset}
                      stats={stats}
                      isWinner={stats.pctChange === bestPct}
                      holding={findHolding(asset.id)}
                      fmt={fmt}
                      fmtDisplay={fmtDisplay}
                    />
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
